"use client";
import { PaginationModel } from "@/types/common";
import { ProductModel } from "@/types/product";
import { useTranslations } from "next-intl";
import ProductsGrid from "./products-grid";
import BrandsCarouselFilters from "../brand/brands-carousel-filters";
import { BrandModel } from "@/types/brand";
import InfiniteScroll from "../ui/infinite-scroll";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  getProductsByBrand,
  getProductsByCategory,
} from "@/services/products.service";

type CacheEntry = {
  productsList: ProductModel[];
  page: number;
  scrollY: number;
};

const CACHE_PREFIX = "products-results:";

const readCache = (key: string): CacheEntry | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch {
    return null;
  }
};

const writeCache = (key: string, entry: CacheEntry) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {}
};

export const ProductsResults = ({
  products,
  title,
  option,
  category,
  brand,
  priceRange,
  displayBrands,
  brands,
  subcategory,
  subCategoryName,
}: {
  products: PaginationModel<ProductModel>;
  title: string;
  option: string;
  category?: string;
  brand?: string;
  priceRange?: string;
  displayBrands?: boolean;
  brands?: BrandModel[] | undefined;
  subcategory?: string;
  subCategoryName?: string;
}) => {
  const t = useTranslations();

  const cacheKey = [
    category ?? "",
    brand ?? "",
    subcategory ?? "",
    option ?? "",
    priceRange ?? "",
  ].join("|");

  const [isLoading, setIsLoading] = useState(false);

  const [state, setState] = useState<{
    page: number;
    productsList: ProductModel[];
  }>(() => {
    const cached = readCache(cacheKey);
    if (cached) return { page: cached.page, productsList: cached.productsList };
    return { page: products.current_page, productsList: products.results };
  });

  const { page, productsList } = state;
  const hasMore = page < products.last_page;

  useEffect(() => {
    const cached = readCache(cacheKey);
    writeCache(cacheKey, {
      productsList,
      page,
      scrollY: cached?.scrollY ?? 0,
    });
  }, [cacheKey, productsList, page]);

  const didRestoreScrollRef = useRef(false);
  useEffect(() => {
    if (didRestoreScrollRef.current) return;
    didRestoreScrollRef.current = true;
    const cached = readCache(cacheKey);
    if (!cached?.scrollY) return;
    requestAnimationFrame(() => {
      window.scrollTo({
        top: cached.scrollY,
        behavior: "instant" as ScrollBehavior,
      });
    });
  }, [cacheKey]);

  useEffect(() => {
    let raf: number | null = null;
    const handler = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const cached = readCache(cacheKey);
        if (cached)
          writeCache(cacheKey, { ...cached, scrollY: window.scrollY });
      });
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => {
      window.removeEventListener("scroll", handler);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [cacheKey]);

  const loadMore = async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const nextPage = page + 1;
    try {
      let response;
      if (category) {
        response = await getProductsByCategory(
          nextPage,
          category,
          subcategory,
          option,
          priceRange,
        );
      } else if (brand) {
        response = await getProductsByBrand(
          nextPage,
          brand,
          option,
          subcategory,
          priceRange,
        );
      }

      if (response) {
        setState((prev) => ({
          productsList: [...prev.productsList, ...response!.results],
          page: nextPage,
        }));
      }
    } catch (error) {
      console.error("Failed to load more products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-5 ">
      <div className="space-y-1">
        <h2 className="font-semibold text-2xl">{title}</h2>
        <div className="flex items-center gap-2">
          {subCategoryName && (
            <div className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
              {subCategoryName}
            </div>
          )}
          <p className="text-muted-foreground text-sm">
            {t("resultsFound", { count: products.total })}
          </p>
        </div>
      </div>
      {displayBrands && brands && brands?.length > 0 && (
        <BrandsCarouselFilters
          brands={brands}
          option={option}
          category={category}
          subcategory={subcategory}
        />
      )}
      <ProductsGrid products={productsList} />

      <InfiniteScroll
        isLoading={isLoading}
        hasMore={hasMore}
        next={loadMore}
        threshold={1}
      >
        {hasMore && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};
